import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import { readAppVersion } from '@/lib/appVersion';
import { ChangelogModal } from '@/features/auth/ChangelogModal';
import { jobLabel } from '@/features/admin/batchFormat';
import type { NotificationItem, NotificationType } from './types';
import {
  reportAppVersion,
  useMarkNotificationsRead,
  useNotifications,
} from './useNotifications';

/**
 * 헤더의 알림 종. (2026-08-19)
 *
 * 🔴 Slack 을 대체하지 않는다
 *   Slack 은 폰으로 밀어주는 것이고 이것은 화면에 들어왔을 때 보는 것이다.
 *   둘 다 있어야 "폰을 안 볼 때" 와 "화면을 볼 때" 가 모두 덮인다.
 *
 * 🔴 권한 없는 알림은 애초에 안 내려온다
 *   화면에서 거르는 것이 아니라 서버가 메뉴 권한으로 가른다.
 *   (NotificationService) 가계부 권한이 없는 사람에게 "월간 리포트가 나왔습니다"
 *   가 뜨면 그 자체가 정보 노출이다.
 */

const ICON: Record<NotificationType, string> = {
  RECOMMENDATION: '📈',
  PAPER_TRADE: '🤖',
  MONTHLY_REPORT: '📊',
  APP_UPDATE: '✨',
  BATCH_FAILURE: '⚠️',
  ERROR: '🔴',
};

interface Props {
  /** 로그인했는가. 안 했으면 아예 부르지 않는다 */
  enabled: boolean;
}

export function NotificationBell({ enabled }: Props) {
  const [open, setOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const { data } = useNotifications(enabled);
  const markRead = useMarkNotificationsRead();
  const ref = useRef<HTMLDivElement>(null);
  const appVersion = readAppVersion(import.meta.env);

  /*
    지금 이 화면의 버전을 서버에 알린다. (2026-08-30, docs/impl/95)

    🔴 백엔드는 화면 버전을 모른다
      그 값은 프론트 빌드 인자라 백엔드 이미지에 없다. 처음 보는 값이면
      서버가 기록하고, 그것이 "새 버전이 배포되었습니다" 알림의 원본이 된다.

    로그인한 뒤에만 부른다 - 인증이 필요한 엔드포인트다.
    실패해도 조용히 넘어간다. 알림 하나가 안 뜰 뿐이고 앱은 멀쩡하다.
  */
  useEffect(() => {
    if (!enabled || appVersion === null) {
      return;
    }
    void reportAppVersion(appVersion.version);
  }, [enabled, appVersion?.version]);

  /*
    바깥을 누르면 닫는다.

    🔴 모달을 쓰지 않는다
      알림은 잠깐 보고 닫는 것이라 화면을 덮을 이유가 없다.
      모달로 만들면 폰에서 뒤로가기와 얽힌다.
  */
  useEffect(() => {
    if (!open) {
      return;
    }
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!enabled) {
    return null;
  }

  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  function toggle() {
    const next = !open;
    setOpen(next);
    // 목록을 봤으면 다 본 것으로 친다. 개별 읽음을 추적하지 않는다.
    if (next && unread > 0) {
      markRead.mutate();
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `알림 ${unread}건` : '알림'}
        className="relative rounded-full px-2 py-1 text-lg hover:bg-surface-hover"
      >
        🔔
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-danger px-1 text-[10px] font-medium leading-4 text-danger-fg">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        /*
          🔴 폰에서는 화면 기준으로 붙인다 (2026-08-30 사용자 피드백)

          그전에는 absolute right-0 하나였다. 그 right-0 은 화면이 아니라
          **종 버튼 기준**인데, 종 오른쪽에 테마 토글과 로그아웃이 더 있다.
          그래서 폭 320px 짜리 패널이 종에서 왼쪽으로 뻗다가 화면 밖으로
          잘려 나갔다. max-w 는 **폭**만 제한하지 위치를 고치지 못한다.

          폰   fixed 로 화면 좌우에 붙인다. 헤더(py-3 + 내용) 아래인 3.5rem 에 띄운다
          PC   기존대로 종 버튼 기준 오른쪽 정렬. 그쪽은 여백이 넉넉해 잘리지 않는다
        */
        <div
          className={
            'fixed inset-x-3 top-14 z-50 rounded-xl border border-line bg-surface shadow-lg ' +
            'sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-1 sm:w-80'
          }
        >
          <div className="border-b border-line px-3 py-2 text-sm font-medium">알림</div>

          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-fg-muted">
              최근 7일간 알릴 것이 없습니다.
            </p>
          ) : (
            <ul className="max-h-96 divide-y divide-line overflow-y-auto">
              {items.map((item, index) => (
                <NotificationRow
                  key={`${item.type}-${item.at}-${index}`}
                  item={item}
                  unread={data ? item.at > data.lastReadAt : false}
                  onNavigate={() => setOpen(false)}
                  // 새 버전 알림은 갈 곳이 없다. 대신 업데이트 내역을 연다
                  onOpenChangelog={() => {
                    setOpen(false);
                    setChangelogOpen(true);
                  }}
                />
              ))}
            </ul>
          )}

          <p className="border-t border-line px-3 py-2 text-[11px] text-fg-subtle">
            최근 7일 · 권한이 있는 메뉴의 알림만 보입니다
          </p>
        </div>
      )}

      {/* 새 버전 알림을 누르면 열린다. 종 바깥에 두어야 종이 닫혀도 남는다 */}
      <ChangelogModal
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        currentVersion={appVersion?.version ?? null}
      />
    </div>
  );
}

function NotificationRow({
  item,
  unread,
  onNavigate,
  onOpenChangelog,
}: {
  item: NotificationItem;
  unread: boolean;
  onNavigate: () => void;
  /** 새 버전 알림을 눌렀을 때. 링크 대신 업데이트 내역을 연다 */
  onOpenChangelog: () => void;
}) {
  const body = (
    <div className={cx('flex gap-2 px-3 py-2', unread && 'bg-brand-weak')}>
      <span className="shrink-0 text-base leading-5">{ICON[item.type]}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{item.title}</p>
        {/* 배치 실패는 detail 에 잡 키가 온다(NotificationService) — 관리자 배치 화면과 같은 한국어 이름으로 (2026-10-16 화면 점검) */}
        {item.detail && (
          <p className="truncate text-xs text-fg-muted">
            {item.type === 'BATCH_FAILURE' ? jobLabel(item.detail) : item.detail}
          </p>
        )}
        <p className="text-[11px] text-fg-subtle">{formatDateTime(item.at)}</p>
      </div>
    </div>
  );

  /*
    🔴 새 버전 알림만 링크가 없고 대신 모달을 연다
      무엇이 바뀌었는지는 changelog 에 있고 그것은 프론트가 갖고 있다.
      서버가 링크를 주려면 내역까지 들고 있어야 하는데, 그러면 배포와 내역이
      따로 놀게 된다 (lib/changelog.ts 를 코드와 함께 두기로 한 것과 같은 이유).
  */
  if (item.type === 'APP_UPDATE') {
    return (
      <li>
        <button
          type="button"
          onClick={onOpenChangelog}
          className="block w-full text-left hover:bg-surface-hover"
        >
          {body}
        </button>
      </li>
    );
  }

  if (!item.link) {
    return <li>{body}</li>;
  }
  return (
    <li>
      <Link to={item.link} onClick={onNavigate} className="block hover:bg-surface-hover">
        {body}
      </Link>
    </li>
  );
}
